import React, { useEffect, useState } from "react";
import { Instagram, Facebook, Mail } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import { useLanguage } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";
import { LANGS } from "@/i18n/translations";
import { scrollToId } from "@/components/SmoothScroll";
import { SOCIAL, MAPS_URL } from "@/lib/constants";
import { registerVisit } from "@/lib/api";

export const Footer = () => {
  const { t, lang, setLang } = useLanguage();
  const { theme } = useTheme();
  const year = new Date().getFullYear();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let vid = localStorage.getItem("estesia_vid");
    if (!vid) {
      vid = crypto.randomUUID();
      localStorage.setItem("estesia_vid", vid);
    }
    registerVisit(vid).then(setStats).catch(() => {});
  }, []);

  const links = [
    { id: "about", label: t("nav.about") },
    { id: "activities", label: t("nav.activities") },
    { id: "library", label: t("nav.library") },
    { id: "contact", label: t("nav.contact") },
  ];

  return (
    <footer className="bg-background border-t border-border py-10" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:items-center">
          <div className="md:col-span-4">
            <div className="flex flex-col items-start">
              <img src={theme === "dark" ? "/images/estesia-logo-night.png" : "/images/estesia-logo-day.png"} alt="Estesia — Zentrum für Spiritistische Studien, Bern · seit 1991" className="h-40 md:h-48 w-auto object-contain object-top block" />
            </div>
          </div>

          <div className="md:col-span-2">
            <nav className="flex flex-col gap-3">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollToId(l.id)}
                  data-testid={`footer-nav-${l.id}`}
                  className="text-left font-sans text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 w-fit"
                >
                  {l.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="md:col-span-2">
            <div className="flex flex-row md:grid md:grid-cols-2 w-fit items-start gap-3">
              <a
                href={SOCIAL.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram-link"
                className="grid place-items-center h-10 w-10 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors duration-300"
              >
                <Instagram size={17} strokeWidth={1.5} />
              </a>
              <a
                href={SOCIAL.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="footer-facebook-link"
                className="grid place-items-center h-10 w-10 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors duration-300"
              >
                <Facebook size={17} strokeWidth={1.5} />
              </a>
              <a
                href="https://chat.whatsapp.com/IxWpimIfyzvGb1LceKtsBT?mode=gi_t"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                data-testid="footer-whatsapp-link"
                className="grid place-items-center h-10 w-10 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors duration-300"
              >
                <WhatsAppIcon size={17} />
              </a>
              <a
                href={`mailto:${SOCIAL.email}`}
                aria-label="E-mail"
                data-testid="footer-email-link"
                className="grid place-items-center h-10 w-10 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors duration-300"
              >
                <Mail size={17} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="md:col-span-4">
            <p className="font-sans uppercase tracking-[0.2em] text-[11px] text-muted-foreground mb-4">Sprache · Language</p>
            <div className="flex flex-wrap gap-2">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`footer-lang-${l.code}`}
                  className={`px-3 py-1.5 rounded-full border text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                    lang === l.code
                      ? "border-primary text-primary"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <p className="mt-6 font-sans text-sm font-light text-muted-foreground">
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-address-link"
                className="underline underline-offset-4 decoration-current/40 hover:text-primary hover:decoration-current transition-[text-decoration-color,color] duration-300"
              >
                Steinerstrasse 14 · {t("contact.floor")}<br />3006 Bern, BE - {t("contact.country")}
              </a>
              <span className="block mt-2 text-xs text-muted-foreground/70 leading-relaxed max-w-[260px]" data-testid="footer-entrance-note">
                {t("contact.entranceNote")}
              </span>
            </p>
          </div>
        </div>

        {stats && (
          <div className="mt-6 text-center" data-testid="footer-visit-counter">
            <p className="font-sans text-sm text-muted-foreground tracking-wide">
              {stats.visitors.toLocaleString(lang === "de" ? "de-CH" : lang)} {t("footer.visitors")} · {stats.visits.toLocaleString(lang === "de" ? "de-CH" : lang)} {t("footer.visits")}
            </p>
            <p className="mt-1.5 font-serif italic text-sm md:text-base text-muted-foreground/80">
              {t("footer.visitorsPoem")}
            </p>
          </div>
        )}

        <div className="mt-6 pt-5 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-sans text-xs text-muted-foreground">© {year} Estesia Bern. {t("footer.rights")}</p>
        </div>
      </div>
    </footer>
  );
};
